import {
  createDriverSchema,
  createMaintenanceSchema,
  createVehicleSchema,
  MAINTENANCE_KINDS,
  VEHICLE_TYPES,
} from '@school-erp/validation';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { api, apiList } from '../../api/http';
import { useAuth } from '../../auth/AuthContext';
import { Notice, errorMessage, primaryButton, selectClass } from '../../components/common';
import { TextField } from '../../components/TextField';
import { t, type MessageKey } from '../../i18n';
import { formatMinor, parseMajor } from '../../lib/money';
import { useCurrency, type Driver, type Message, type Vehicle } from './shared';

interface ExpiryItem {
  kind: string;
  subject: string;
  subjectId: string;
  date: string;
  daysLeft: number;
}
interface MaintenanceRecord {
  id: string;
  kind: string;
  performedOn: string;
  costMinor: number;
  description: string;
  nextDueOn: string | null;
}
type Check = { success: boolean; error?: { issues: { path: PropertyKey[]; message: string }[] } };

const blankVehicle = { reg: '', capacity: '', type: 'bus', insurance: '', fitness: '' };
const blankDriver = { name: '', phone: '', licence: '', expiry: '' };
const blankWork = { kind: 'service', on: '', cost: '', description: '', next: '' };

export function FleetTab({ schoolId }: { schoolId: string }) {
  const { can } = useAuth();
  const queryClient = useQueryClient();
  const currency = useCurrency(schoolId);
  const [vehicleForm, setVehicleForm] = useState(blankVehicle);
  const [driverForm, setDriverForm] = useState(blankDriver);
  const [workForm, setWorkForm] = useState(blankWork);
  const [vehicleId, setVehicleId] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [message, setMessage] = useState<Message>(null);

  const vehicles = useQuery({
    queryKey: ['transport', schoolId, 'vehicles'],
    queryFn: () =>
      apiList<Vehicle>(`/schools/${schoolId}/transport/vehicles`, { query: { pageSize: 100 } }),
  });
  const drivers = useQuery({
    queryKey: ['transport', schoolId, 'drivers'],
    queryFn: () =>
      apiList<Driver>(`/schools/${schoolId}/transport/drivers`, { query: { pageSize: 100 } }),
  });
  const expiring = useQuery({
    queryKey: ['transport', schoolId, 'expiring'],
    queryFn: () =>
      api<ExpiryItem[]>(`/schools/${schoolId}/transport/expiring`, { query: { days: 30 } }),
  });
  const selected = vehicleId || vehicles.data?.data[0]?.id || '';
  const maintenance = useQuery({
    queryKey: ['transport', schoolId, 'maintenance', selected],
    queryFn: () =>
      api<{ records: MaintenanceRecord[] }>(
        `/schools/${schoolId}/transport/vehicles/${selected}/maintenance`,
      ),
    enabled: Boolean(selected),
  });

  const refresh = () => void queryClient.invalidateQueries({ queryKey: ['transport', schoolId] });
  const useCreate = (path: string, done: () => void, ok: MessageKey) =>
    useMutation({
      mutationFn: (body: object) =>
        api(`/schools/${schoolId}/transport/${path}`, { method: 'POST', body }),
      onSuccess: () => {
        done();
        setMessage({ kind: 'ok', text: t(ok) });
        refresh();
      },
      onError: (e) => setMessage({ kind: 'error', text: errorMessage(e) }),
    });
  const addVehicle = useCreate('vehicles', () => setVehicleForm(blankVehicle), 'tr.vehicleAdded');
  const addDriver = useCreate('drivers', () => setDriverForm(blankDriver), 'tr.driverAdded');
  const addWork = useCreate(
    `vehicles/${selected}/maintenance`,
    () => setWorkForm(blankWork),
    'tr.workAdded',
  );

  const check = (parsed: Check, extra: Record<string, string> = {}) => {
    const next: Record<string, string> = { ...extra };
    if (!parsed.success)
      for (const i of parsed.error?.issues ?? []) next[String(i.path[0])] = i.message;
    setErrors(next);
    return parsed.success && Object.keys(next).length === 0;
  };

  return (
    <section aria-label={t('tr.tab.fleet')} className="space-y-8">
      {message && <Notice kind={message.kind}>{message.text}</Notice>}

      <div className="space-y-2">
        <h2 className="text-lg font-semibold">{t('tr.expiring')}</h2>
        {expiring.data?.length === 0 && <p className="text-slate-600">{t('tr.nothingDue')}</p>}
        <ul className="space-y-1 text-sm">
          {expiring.data?.map((x) => (
            <li
              key={`${x.kind}-${x.subjectId}-${x.date}`}
              className={x.daysLeft < 0 ? 'font-medium text-red-700' : ''}
            >
              {t(`tr.expiry.${x.kind}` as MessageKey)}: {x.subject} · {x.date} ·{' '}
              {x.daysLeft < 0
                ? t('tr.expiredDays', { days: -x.daysLeft })
                : t('tr.inDays', { days: x.daysLeft })}
            </li>
          ))}
        </ul>
      </div>

      <div className="space-y-3">
        <h2 className="text-lg font-semibold">{t('tr.vehicles')}</h2>
        {vehicles.data?.data.length === 0 && <p className="text-slate-600">{t('tr.noVehicles')}</p>}
        <ul className="divide-y divide-slate-100 rounded-lg border border-slate-200 bg-white text-sm">
          {vehicles.data?.data.map((v) => (
            <li key={v.id} className="flex flex-wrap justify-between gap-2 p-3">
              <span>
                <span className="font-medium">{v.registrationNumber}</span> ·{' '}
                {t(`tr.type.${v.vehicleType}` as MessageKey)}
              </span>
              <span>
                {t('tr.capacity', { n: v.capacity })} · {t(`tr.status.${v.status}` as MessageKey)}
              </span>
            </li>
          ))}
        </ul>
        {can('transport.manage', schoolId) && (
          <form
            noValidate
            aria-label={t('tr.addVehicle')}
            className="grid gap-4 rounded-lg border border-slate-200 bg-white p-4 sm:grid-cols-3"
            onSubmit={(e) => {
              e.preventDefault();
              const parsed = createVehicleSchema.safeParse({
                registrationNumber: vehicleForm.reg,
                capacity: Number(vehicleForm.capacity),
                vehicleType: vehicleForm.type,
                insuranceExpiry: vehicleForm.insurance || null,
                fitnessExpiry: vehicleForm.fitness || null,
              });
              if (check(parsed) && parsed.success) {
                setMessage(null);
                addVehicle.mutate(parsed.data);
              }
            }}
          >
            <TextField
              label={t('tr.registration')}
              value={vehicleForm.reg}
              error={errors.registrationNumber}
              onChange={(e) => setVehicleForm({ ...vehicleForm, reg: e.target.value })}
            />
            <TextField
              label={t('tr.seats')}
              inputMode="numeric"
              value={vehicleForm.capacity}
              error={errors.capacity}
              onChange={(e) => setVehicleForm({ ...vehicleForm, capacity: e.target.value })}
            />
            <div>
              <label htmlFor="vehicle-type" className="block text-sm font-medium">
                {t('tr.vehicleType')}
              </label>
              <select
                id="vehicle-type"
                className={selectClass}
                value={vehicleForm.type}
                onChange={(e) => setVehicleForm({ ...vehicleForm, type: e.target.value })}
              >
                {VEHICLE_TYPES.map((x) => (
                  <option key={x} value={x}>
                    {t(`tr.type.${x}` as MessageKey)}
                  </option>
                ))}
              </select>
            </div>
            <TextField
              type="date"
              label={t('tr.insuranceExpiry')}
              value={vehicleForm.insurance}
              error={errors.insuranceExpiry}
              onChange={(e) => setVehicleForm({ ...vehicleForm, insurance: e.target.value })}
            />
            <TextField
              type="date"
              label={t('tr.fitnessExpiry')}
              value={vehicleForm.fitness}
              error={errors.fitnessExpiry}
              onChange={(e) => setVehicleForm({ ...vehicleForm, fitness: e.target.value })}
            />
            <div className="flex items-end">
              <button type="submit" className={primaryButton} disabled={addVehicle.isPending}>
                {t('tr.addVehicle')}
              </button>
            </div>
          </form>
        )}
      </div>

      <div className="space-y-3">
        <h2 className="text-lg font-semibold">{t('tr.drivers')}</h2>
        {drivers.data?.data.length === 0 && <p className="text-slate-600">{t('tr.noDrivers')}</p>}
        <ul className="divide-y divide-slate-100 rounded-lg border border-slate-200 bg-white text-sm">
          {drivers.data?.data.map((d) => (
            <li key={d.id} className="flex flex-wrap justify-between gap-2 p-3">
              <span>
                <span className="font-medium">{d.fullName}</span> · {d.phone}
              </span>
              <span>{t('tr.licenceUntil', { date: d.licenceExpiry })}</span>
            </li>
          ))}
        </ul>
        {can('transport.manage', schoolId) && (
          <form
            noValidate
            aria-label={t('tr.addDriver')}
            className="grid gap-4 rounded-lg border border-slate-200 bg-white p-4 sm:grid-cols-3"
            onSubmit={(e) => {
              e.preventDefault();
              const parsed = createDriverSchema.safeParse({
                fullName: driverForm.name,
                phone: driverForm.phone,
                licenceNumber: driverForm.licence,
                licenceExpiry: driverForm.expiry,
              });
              if (check(parsed) && parsed.success) {
                setMessage(null);
                addDriver.mutate(parsed.data);
              }
            }}
          >
            <TextField
              label={t('tr.driverName')}
              value={driverForm.name}
              error={errors.fullName}
              onChange={(e) => setDriverForm({ ...driverForm, name: e.target.value })}
            />
            <TextField
              label={t('tr.phone')}
              value={driverForm.phone}
              error={errors.phone}
              onChange={(e) => setDriverForm({ ...driverForm, phone: e.target.value })}
            />
            <TextField
              label={t('tr.licenceNumber')}
              value={driverForm.licence}
              error={errors.licenceNumber}
              onChange={(e) => setDriverForm({ ...driverForm, licence: e.target.value })}
            />
            <TextField
              type="date"
              label={t('tr.licenceExpiry')}
              value={driverForm.expiry}
              error={errors.licenceExpiry}
              onChange={(e) => setDriverForm({ ...driverForm, expiry: e.target.value })}
            />
            <div className="flex items-end">
              <button type="submit" className={primaryButton} disabled={addDriver.isPending}>
                {t('tr.addDriver')}
              </button>
            </div>
          </form>
        )}
      </div>

      {(vehicles.data?.data.length ?? 0) > 0 && (
        <div className="space-y-3">
          <h2 className="text-lg font-semibold">{t('tr.maintenance')}</h2>
          <div>
            <label htmlFor="maint-vehicle" className="block text-sm font-medium">
              {t('tr.vehicle')}
            </label>
            <select
              id="maint-vehicle"
              className={selectClass}
              value={selected}
              onChange={(e) => setVehicleId(e.target.value)}
            >
              {vehicles.data?.data.map((v) => (
                <option key={v.id} value={v.id}>
                  {v.registrationNumber}
                </option>
              ))}
            </select>
          </div>
          {maintenance.data?.records.length === 0 && (
            <p className="text-slate-600">{t('tr.noWork')}</p>
          )}
          <ul className="divide-y divide-slate-100 rounded-lg border border-slate-200 bg-white text-sm">
            {maintenance.data?.records.map((m) => (
              <li key={m.id} className="flex flex-wrap justify-between gap-2 p-3">
                <span>
                  {m.performedOn} · {t(`tr.work.${m.kind}` as MessageKey)} · {m.description}
                </span>
                <span>
                  {formatMinor(m.costMinor, currency)}
                  {m.nextDueOn ? ` · ${t('tr.nextDue', { date: m.nextDueOn })}` : ''}
                </span>
              </li>
            ))}
          </ul>
          {can('transport.maintenance', schoolId) && (
            <form
              noValidate
              aria-label={t('tr.addWork')}
              className="grid gap-4 rounded-lg border border-slate-200 bg-white p-4 sm:grid-cols-3"
              onSubmit={(e) => {
                e.preventDefault();
                const costMinor =
                  workForm.cost.trim() === '' ? 0 : parseMajor(workForm.cost, currency);
                const parsed = createMaintenanceSchema.safeParse({
                  kind: workForm.kind,
                  performedOn: workForm.on,
                  costMinor,
                  description: workForm.description,
                  nextDueOn: workForm.next || null,
                });
                const extra: Record<string, string> =
                  costMinor === null ? { costMinor: t('tr.invalid') } : {};
                if (check(parsed, extra) && parsed.success) {
                  setMessage(null);
                  addWork.mutate(parsed.data);
                }
              }}
            >
              <div>
                <label htmlFor="work-kind" className="block text-sm font-medium">
                  {t('tr.workKind')}
                </label>
                <select
                  id="work-kind"
                  className={selectClass}
                  value={workForm.kind}
                  onChange={(e) => setWorkForm({ ...workForm, kind: e.target.value })}
                >
                  {MAINTENANCE_KINDS.map((k) => (
                    <option key={k} value={k}>
                      {t(`tr.work.${k}` as MessageKey)}
                    </option>
                  ))}
                </select>
              </div>
              <TextField
                type="date"
                label={t('tr.workDate')}
                value={workForm.on}
                error={errors.performedOn}
                onChange={(e) => setWorkForm({ ...workForm, on: e.target.value })}
              />
              <TextField
                label={t('tr.cost', { currency })}
                inputMode="decimal"
                value={workForm.cost}
                error={errors.costMinor}
                onChange={(e) => setWorkForm({ ...workForm, cost: e.target.value })}
              />
              <TextField
                className="sm:col-span-2"
                label={t('tr.workDescription')}
                value={workForm.description}
                error={errors.description}
                onChange={(e) => setWorkForm({ ...workForm, description: e.target.value })}
              />
              <TextField
                type="date"
                label={t('tr.nextDueLabel')}
                value={workForm.next}
                error={errors.nextDueOn}
                onChange={(e) => setWorkForm({ ...workForm, next: e.target.value })}
              />
              <div className="flex items-end">
                <button type="submit" className={primaryButton} disabled={addWork.isPending}>
                  {t('tr.addWork')}
                </button>
              </div>
            </form>
          )}
        </div>
      )}
    </section>
  );
}
